import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, TrendingUp, RefreshCw, MessageCircle, Archive, Eye, EyeOff
} from 'lucide-react';
import ClientTodoEditor from '../components/admin/ClientTodoEditor';
import TodoList from '../components/admin/TodoList';
import MilestoneEditor from '../components/admin/MilestoneEditor';
import RecentUpdates from '../components/admin/RecentUpdates';
import SimpleChatThread from '../components/SimpleChatThread';
import RecentUpdatesEditor from '../components/admin/RecentUpdatesEditor';
import InternalNotes from '../components/admin/InternalNotes';
import FileUpload from '../components/FileUpload';
import FilesList from '../components/FilesList';
import { formatDate } from '../utils/dateFormat';
import { STAGE_MAP } from '../utils/stageMap';
import { AGENTS, getAgentEmail } from '../config/agents';
import { PROJECT_TYPES, getDefaultTodos } from '../config/projectTemplates';
import { STATUS_OPTIONS, getStatusConfig, isArchived } from '../config/statusConfig';
import { getMilestoneTemplate } from '../config/milestoneTemplates';
import '../styles/chat.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;

const STAGES = Object.entries(STAGE_MAP).map(([value, data]) => ({
  value,
  label: data.label,
  percent: data.pct,
  description: data.helper
}));

const CAdminPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [passwordResetModal, setPasswordResetModal] = useState({ show: false, password: '', clientId: '' });
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // 'all', 'assigned', 'unassigned'
  const [executives, setExecutives] = useState([]);
  const [assigningExecutive, setAssigningExecutive] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleResetPassword = async () => {
    if (!selectedClient) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/client/${selectedClient.client_id}/reset-password`, {
        method: 'POST',
        headers: {
          'x-admin-key': ADMIN_KEY
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPasswordResetModal({
          show: true,
          password: data.new_password,
          clientId: data.client_id
        });
        showToast('Password reset successfully');
      } else {
        showToast('Failed to reset password', 'error');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      showToast('Error resetting password', 'error');
    }
  };

  const copyPasswordToClipboard = () => {
    navigator.clipboard.writeText(passwordResetModal.password);
    showToast('Password copied to clipboard');
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/clients`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data);
        
        // Fetch unread counts for each client (Chat v2)
        const counts = {};
        for (const client of data) {
          const msgRes = await fetch(`${BACKEND_URL}/api/clients/${client.client_id}/messages`);
          if (msgRes.ok) {
            const messages = await msgRes.json();
            // Count messages from client that haven't been read by staff
            const unread = messages.filter(m => m.from === 'client' && m.read_by_staff === false).length;
            if (unread > 0) {
              counts[client.client_id] = unread;
            }
          }
        }
        setUnreadCounts(counts);
      } else {
        showToast('Failed to fetch clients', 'error');
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      showToast('Error fetching clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutives = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/executives/all`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      if (response.ok) {
        const data = await response.json();
        setExecutives(data.executives || []);
      }
    } catch (error) {
      console.error('Error fetching executives:', error);
    }
  };

  const handleAssignExecutive = async (executiveId) => {
    if (!selectedClient || assigningExecutive) return;
    
    setAssigningExecutive(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}/executive`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ executive_id: executiveId || null })
      });

      if (response.ok) {
        const data = await response.json();
        // Update clients list
        setClients(clients.map(c => 
          c.client_id === selectedClient.client_id 
            ? { ...c, executive_id: data.executive_id, executive_name: data.executive_name }
            : c
        ));
        // Update selected client
        setSelectedClient(prev => ({
          ...prev,
          executive_id: data.executive_id,
          executive_name: data.executive_name
        }));
        showToast(executiveId ? `Assigned to ${data.executive_name}` : 'Executive unassigned');
      } else {
        showToast('Failed to assign executive', 'error');
      }
    } catch (error) {
      console.error('Error assigning executive:', error);
      showToast('Error assigning executive', 'error');
    } finally {
      setAssigningExecutive(false);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchExecutives();
  }, []);

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setEditData({
      status_stage: client.status_stage || 'INTAKE',
      status: client.status || 'New',
      agent: client.agent || { name: '', role: 'Project Manager', email: '' },
      invoice_url: client.invoice_url || '',
      project_type: client.project_type || '',
      kickoff_date: client.kickoff_date || '',
      first_draft_date: client.first_draft_date || '',
      launch_start_date: client.launch_start_date || '',
      launch_end_date: client.launch_end_date || ''
    });
    
    // Mark client messages as read (Chat v2)
    try {
      await fetch(`${BACKEND_URL}/api/clients/${client.client_id}/messages/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ who: 'staff' })
      });
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
    
    // Clear unread badge for this client
    if (unreadCounts[client.client_id]) {
      const newCounts = { ...unreadCounts };
      delete newCounts[client.client_id];
      setUnreadCounts(newCounts);
    }
  };

  const handleSaveChanges = async () => {
    if (!selectedClient || saving) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const updatedClient = await response.json();
        setClients(clients.map(c => 
          c.client_id === selectedClient.client_id ? updatedClient : c
        ));
        setSelectedClient(updatedClient);
        setEditData({
          status_stage: updatedClient.status_stage || 'INTAKE',
          status: updatedClient.status || 'New',
          agent: updatedClient.agent || { name: '', role: 'Project Manager', email: '' },
          invoice_url: updatedClient.invoice_url || '',
          project_type: updatedClient.project_type || '',
          kickoff_date: updatedClient.kickoff_date || '',
          first_draft_date: updatedClient.first_draft_date || '',
          launch_start_date: updatedClient.launch_start_date || '',
          launch_end_date: updatedClient.launch_end_date || ''
        });
        showToast('Changes saved successfully');
      } else {
        showToast('Failed to save changes', 'error');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast('Error saving changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const refreshClient = async () => {
    if (!selectedClient) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}`, {
        headers: { 'x-admin-key': ADMIN_KEY }
      });
      
      if (response.ok) {
        const updatedClient = await response.json();
        setSelectedClient(updatedClient);
        setClients(clients.map(c => 
          c.client_id === selectedClient.client_id ? updatedClient : c
        ));
        
        // Auto-seed milestones if empty and project type exists
        await checkAndSeedMilestones(updatedClient);
      }
    } catch (error) {
      console.error('Error refreshing client:', error);
    }
  };
  
  const checkAndSeedMilestones = async (client) => {
    // Only seed if milestones are empty and project type exists
    const milestones = client.milestones || [];
    const projectType = client.project_type;
    
    if (milestones.length === 0 && projectType) {
      try {
        const template = getMilestoneTemplate(projectType);
        const response = await fetch(`${BACKEND_URL}/api/clients/${client.client_id}/milestones/seed-from-template`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-admin-key': ADMIN_KEY
          },
          body: JSON.stringify({ milestones: template })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.added > 0) {
            // Fetch updated client data without recursion
            const refreshResponse = await fetch(`${BACKEND_URL}/api/clients/${client.client_id}`, {
              headers: { 'x-admin-key': ADMIN_KEY }
            });
            if (refreshResponse.ok) {
              const updatedClient = await refreshResponse.json();
              setSelectedClient(updatedClient);
              setClients(prevClients => 
                prevClients.map(c => c.client_id === updatedClient.client_id ? updatedClient : c)
              );
            }
          }
        }
      } catch (err) {
        console.error('Error seeding milestones:', err);
      }
    }
  };

  const handleAddTodo = () => {
    console.log('Add To-Do button clicked'); // Debug log
    setEditingTodo(null);
    setShowTodoModal(true);
  };

  const handleEditTodo = (todo) => {
    console.log('Edit To-Do clicked', todo); // Debug log
    setEditingTodo(todo);
    setShowTodoModal(true);
  };

  const handleDeleteTodo = async (todoId) => {
    if (!confirm('Are you sure you want to delete this to-do?')) return;

    try {
      const updatedTodos = selectedClient.client_todos.filter(t => t.id !== todoId);
      
      const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': ADMIN_KEY
        },
        body: JSON.stringify({ client_todos: updatedTodos })
      });

      if (response.ok) {
        await refreshClient();
        showToast('To-do deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting todo:', error);
      showToast('Failed to delete to-do', 'error');
    }
  };

  const handleDuplicateTodo = (todo) => {
    setEditingTodo({
      ...todo,
      id: undefined,
      title: `${todo.title} (Copy)`
    });
    setShowTodoModal(true);
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F8FAFC'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid #E2E8F0', 
            borderTop: '3px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#64748B' }}>Loading clients...</p>
        </div>
      </div>
    );
  }

  const currentStage = STAGES.find(s => s.value === editData?.status_stage) || STAGES[0];

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: '500',
          background: toast.type === 'success' ? '#10B981' : '#EF4444',
          color: '#fff'
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '32px 24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Users size={32} color="#fff" />
            <div>
              <h1 style={{ margin: 0, color: '#fff', fontSize: '28px', fontWeight: '700' }}>
                Client Dashboard Admin
              </h1>
              <p style={{ margin: '4px 0 0 0', color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                Manage client projects, progress, and communications
              </p>
            </div>
          </div>
          <Link to="/admin" style={{ textDecoration: 'none' }}>
            <button style={{
              padding: '10px 20px',
              background: 'rgba(255,255,255,0.2)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
              backdropFilter: 'blur(10px)'
            }}
            onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
            >
              Back to Intake Admin
            </button>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '32px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: selectedClient ? '280px 1fr' : '1fr', gap: '24px' }}>
          {/* Clients List */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid #E2E8F0',
            height: 'fit-content',
            maxHeight: 'calc(100vh - 250px)',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1E293B' }}>
                Clients ({clients.filter(c => {
                  if (!showArchived && isArchived(c.status)) return false;
                  if (assignmentFilter === 'assigned') return !!c.executive_id;
                  if (assignmentFilter === 'unassigned') return !c.executive_id;
                  return true;
                }).length})
              </h2>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  style={{
                    padding: '6px 10px',
                    background: showArchived ? '#667eea' : '#F1F5F9',
                    color: showArchived ? '#fff' : '#475569',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                  title={showArchived ? "Hide archived" : "Show archived"}
                >
                  {showArchived ? <EyeOff size={14} /> : <Eye size={14} />}
                  {showArchived ? 'Hide' : 'Show'} Archived
                </button>
                <button
                  onClick={fetchClients}
                  style={{
                    padding: '6px',
                    background: '#F1F5F9',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Refresh"
                >
                  <RefreshCw size={14} color="#475569" />
                </button>
              </div>
            </div>

            {/* Assignment Filter Tabs */}
            <div style={{ 
              display: 'flex', 
              gap: '4px', 
              marginBottom: '16px',
              background: '#F1F5F9',
              padding: '4px',
              borderRadius: '8px'
            }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'assigned', label: 'Assigned' },
                { key: 'unassigned', label: 'Unassigned' }
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setAssignmentFilter(tab.key)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: assignmentFilter === tab.key ? '#fff' : 'transparent',
                    color: assignmentFilter === tab.key ? '#1E293B' : '#64748B',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '600',
                    transition: 'all 0.2s',
                    boxShadow: assignmentFilter === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {clients.filter(c => {
              if (!showArchived && isArchived(c.status)) return false;
              if (assignmentFilter === 'assigned') return !!c.executive_id;
              if (assignmentFilter === 'unassigned') return !c.executive_id;
              return true;
            }).length === 0 ? (
              <p style={{ color: '#94A3B8', textAlign: 'center', padding: '32px 0', fontSize: '14px' }}>
                {assignmentFilter === 'assigned' ? 'No assigned clients' : 
                 assignmentFilter === 'unassigned' ? 'No unassigned clients' :
                 showArchived ? 'No archived clients' : 'No active clients found'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {clients.filter(c => {
                  if (!showArchived && isArchived(c.status)) return false;
                  if (assignmentFilter === 'assigned') return !!c.executive_id;
                  if (assignmentFilter === 'unassigned') return !c.executive_id;
                  return true;
                }).map((client) => {
                  const statusConfig = getStatusConfig(client.status);
                  return (
                  <div
                    key={client.client_id}
                    onClick={() => handleSelectClient(client)}
                    style={{
                      padding: '12px',
                      border: '1px solid',
                      borderColor: selectedClient?.client_id === client.client_id ? '#667eea' : '#E2E8F0',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: selectedClient?.client_id === client.client_id ? '#F5F3FF' : '#fff',
                      position: 'relative'
                    }}
                    onMouseOver={(e) => {
                      if (selectedClient?.client_id !== client.client_id) {
                        e.currentTarget.style.borderColor = '#CBD5E1';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedClient?.client_id !== client.client_id) {
                        e.currentTarget.style.borderColor = '#E2E8F0';
                      }
                    }}
                  >
                    {unreadCounts[client.client_id] && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        background: '#EF4444',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {unreadCounts[client.client_id]}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748B' }}>
                        {client.client_id}
                      </div>
                      <div style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '600',
                        color: statusConfig.color,
                        backgroundColor: statusConfig.bgColor
                      }}>
                        {statusConfig.label}
                      </div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1E293B', marginBottom: '2px' }}>
                      {client.full_name || client.client_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '6px' }}>
                      {client.business_name}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      gap: '6px',
                      paddingTop: '6px',
                      borderTop: '1px solid #F1F5F9'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <TrendingUp size={12} color="#667eea" />
                        <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>
                          {client.progress_percent || 0}%
                        </span>
                      </div>
                      {client.executive_name ? (
                        <span style={{ 
                          fontSize: '10px', 
                          color: '#10B981', 
                          fontWeight: '600',
                          padding: '2px 6px',
                          background: '#ECFDF5',
                          borderRadius: '4px'
                        }}>
                          {client.executive_name.split(' ')[0]}
                        </span>
                      ) : (
                        <span style={{ 
                          fontSize: '10px', 
                          color: '#94A3B8', 
                          fontWeight: '500'
                        }}>
                          Unassigned
                        </span>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>

          {/* Client Details Panel */}
          {selectedClient && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Client Info & Progress Card */}
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{ marginBottom: '20px' }}>
                  <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: '#1E293B' }}>
                    {selectedClient.full_name || selectedClient.client_name}
                  </h2>
                  <p style={{ margin: 0, fontSize: '14px', color: '#64748B' }}>
                    {selectedClient.client_id} • {selectedClient.business_name}
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  {/* Progress Stage */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Progress Stage
                    </label>
                    <select
                      value={editData.status_stage}
                      onChange={(e) => setEditData({ ...editData, status_stage: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        background: '#fff'
                      }}
                    >
                      {STAGES.map(stage => (
                        <option key={stage.value} value={stage.value}>
                          {stage.label} ({stage.percent}%)
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: '12px', color: '#64748B', marginTop: '4px' }}>
                      {currentStage.description}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Status (High Level)
                    </label>
                    <select
                      value={editData.status}
                      onChange={(e) => setEditData({ ...editData, status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        background: '#fff'
                      }}
                    >
                      {STATUS_OPTIONS.map(statusOpt => (
                        <option key={statusOpt.value} value={statusOpt.value}>
                          {statusOpt.label}
                        </option>
                      ))}
                    </select>
                    <div style={{ 
                      fontSize: '11px', 
                      color: getStatusConfig(editData.status).color,
                      marginTop: '4px',
                      fontWeight: '600'
                    }}>
                      Status: {getStatusConfig(editData.status).label}
                    </div>
                  </div>

                  {/* Agent Name - Dropdown */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Agent Name
                    </label>
                    <select
                      value={editData.agent?.name || ''}
                      onChange={(e) => {
                        const agentName = e.target.value;
                        const agentEmail = getAgentEmail(agentName);
                        setEditData({ 
                          ...editData, 
                          agent: { 
                            ...editData.agent, 
                            name: agentName,
                            email: agentEmail 
                          }
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        background: '#fff'
                      }}
                    >
                      <option value="">Select Agent</option>
                      {AGENTS.map(agent => (
                        <option key={agent.name} value={agent.name}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Agent Email - Auto-populated */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Agent Email
                    </label>
                    <input
                      type="email"
                      value={editData.agent?.email || ''}
                      onChange={(e) => setEditData({ 
                        ...editData, 
                        agent: { ...editData.agent, email: e.target.value }
                      })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        background: '#F8FAFC'
                      }}
                      placeholder="Auto-filled from agent name"
                    />
                    <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px' }}>
                      Email auto-populates when you select an agent
                    </div>
                  </div>

                  {/* Executive Assignment */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                      Assigned Executive
                    </label>
                    <select
                      value={selectedClient.executive_id || ''}
                      onChange={(e) => handleAssignExecutive(e.target.value || null)}
                      disabled={assigningExecutive}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        background: assigningExecutive ? '#F1F5F9' : '#fff',
                        cursor: assigningExecutive ? 'wait' : 'pointer'
                      }}
                    >
                      <option value="">No Executive Assigned</option>
                      {executives.map(exec => (
                        <option key={exec.id} value={exec.id}>
                          {exec.full_name} ({exec.title || 'Executive'})
                        </option>
                      ))}
                    </select>
                    {selectedClient.executive_name && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#10B981',
                        marginTop: '4px',
                        fontWeight: '600'
                      }}>
                        ✓ Assigned to {selectedClient.executive_name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Project Type or Services */}
                <div style={{ marginTop: '16px' }}>
                  {selectedClient.request_category === 'mini_services' ? (
                    <>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          Services Ordered
                          <span style={{ padding: '2px 6px', background: '#FEF2F2', color: '#DC2626', borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>
                            Mini Services
                          </span>
                        </span>
                      </label>
                      <div style={{ 
                        padding: '10px', 
                        background: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#1E293B'
                      }}>
                        {selectedClient.selected_services && selectedClient.selected_services.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {selectedClient.selected_services.map((service, idx) => (
                              <li key={idx} style={{ marginBottom: '4px' }}>{service}</li>
                            ))}
                          </ul>
                        ) : (
                          'No services specified'
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          Project Type
                          <span style={{ padding: '2px 6px', background: '#EFF6FF', color: '#1E3A8A', borderRadius: '4px', fontSize: '10px', fontWeight: '600' }}>
                            Full Project
                          </span>
                        </span>
                      </label>
                      <div style={{ 
                        padding: '10px', 
                        background: '#F8FAFC', 
                        border: '1px solid #E2E8F0', 
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#1E293B'
                      }}>
                        {selectedClient.project_type || 'Not specified'}
                        {selectedClient.project_type_notes && (
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748B', fontStyle: 'italic' }}>
                            Notes: {selectedClient.project_type_notes}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Keep old project type selector hidden for backward compatibility */}
                <input type="hidden" value={editData.project_type || ''} onChange={() => {}} />
                
                {/* Old Project Type Dropdown (commented out, keeping for reference) */}
                {/* <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Project Type
                  </label>
                  <select
                    value={editData.project_type || ''}
                    onChange={async (e) => {
                      const newProjectType = e.target.value;
                      const prevProjectType = editData.project_type;
                      
                      setEditData({ ...editData, project_type: newProjectType });
                      
                      // If project type changed, offer to seed defaults
                      if (newProjectType && newProjectType !== prevProjectType) {
                        const shouldSeed = window.confirm(
                          `Would you like to add default To-Dos and Timeline Milestones for "${newProjectType}"? This will not overwrite existing items.`
                        );
                        
                        if (shouldSeed) {
                          let addedTodos = 0;
                          let addedMilestones = 0;
                          let errors = [];
                          
                          // Seed To-Dos
                          const defaultTodos = getDefaultTodos(newProjectType);
                          if (defaultTodos.length > 0) {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}/todos/seed-from-template`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'x-admin-key': ADMIN_KEY
                                },
                                body: JSON.stringify({ todos: defaultTodos })
                              });
                              
                              if (response.ok) {
                                const result = await response.json();
                                console.log('To-Do seeding response:', result);
                                addedTodos = result.added || 0;
                              } else {
                                errors.push('To-Dos');
                              }
                            } catch (err) {
                              console.error('Error seeding todos:', err);
                              errors.push('To-Dos');
                            }
                          }
                          
                          // Seed Milestones
                          const milestoneTemplate = getMilestoneTemplate(newProjectType);
                          if (milestoneTemplate.length > 0) {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}/milestones/seed-from-template`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'x-admin-key': ADMIN_KEY
                                },
                                body: JSON.stringify({ milestones: milestoneTemplate })
                              });
                              
                              if (response.ok) {
                                const result = await response.json();
                                console.log('Milestone seeding response:', result);
                                addedMilestones = result.added || 0;
                              } else {
                                errors.push('Milestones');
                              }
                            } catch (err) {
                              console.error('Error seeding milestones:', err);
                              errors.push('Milestones');
                            }
                          }
                          
                          // Show summary based on results
                          if (errors.length > 0) {
                            showToast(`❌ Error adding ${errors.join(' and ')}`);
                          } else if (addedTodos > 0 || addedMilestones > 0) {
                            const parts = [];
                            if (addedTodos > 0) parts.push(`${addedTodos} To-Dos`);
                            if (addedMilestones > 0) parts.push(`${addedMilestones} Milestones`);
                            showToast(`✓ Added ${parts.join(' and ')}`);
                          } else {
                            // Check if it was explicitly skipped due to existing items
                            showToast('⚠️ Items already exist, nothing added. Use "Apply Defaults" button to force.');
                          }
                          
                          // Always refresh to show current state
                          await refreshClient();
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      background: '#fff'
                    }}
                  >
                    <option value="">Select Project Type</option>
                    {PROJECT_TYPES.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Changing this will prompt to add defaults</span>
                    {editData.project_type && (
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = window.confirm(
                            `Apply default To-Dos and Milestones for "${editData.project_type}"? This will skip the "already exist" check.`
                          );
                          
                          if (confirmed) {
                            let addedTodos = 0;
                            let addedMilestones = 0;
                            
                            // Force seed To-Dos
                            const defaultTodos = getDefaultTodos(editData.project_type);
                            if (defaultTodos.length > 0) {
                              try {
                                const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}/todos/seed-from-template`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'x-admin-key': ADMIN_KEY
                                  },
                                  body: JSON.stringify({ todos: defaultTodos, force: true })
                                });
                                
                                if (response.ok) {
                                  const result = await response.json();
                                  addedTodos = result.added || 0;
                                }
                              } catch (err) {
                                console.error('Error force-seeding todos:', err);
                              }
                            }
                            
                            // Force seed Milestones
                            const milestoneTemplate = getMilestoneTemplate(editData.project_type);
                            if (milestoneTemplate.length > 0) {
                              try {
                                const response = await fetch(`${BACKEND_URL}/api/clients/${selectedClient.client_id}/milestones/seed-from-template`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'x-admin-key': ADMIN_KEY
                                  },
                                  body: JSON.stringify({ milestones: milestoneTemplate, force: true })
                                });
                                
                                if (response.ok) {
                                  const result = await response.json();
                                  addedMilestones = result.added || 0;
                                }
                              } catch (err) {
                                console.error('Error force-seeding milestones:', err);
                              }
                            }
                            
                            if (addedTodos > 0 || addedMilestones > 0) {
                              const parts = [];
                              if (addedTodos > 0) parts.push(`${addedTodos} To-Dos`);
                              if (addedMilestones > 0) parts.push(`${addedMilestones} Milestones`);
                              showToast(`✓ Force-applied ${parts.join(' and ')}`);
                            } else {
                              showToast('No new items added');
                            }
                            
                            await refreshClient();
                          }
                        }}
                        style={{
                          padding: '2px 8px',
                          fontSize: '11px',
                          background: 'transparent',
                          color: '#667eea',
                          border: '1px solid #667eea',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Apply Defaults
                      </button>
                    )}
                  </div>
                </div> */}

                {/* Invoice URL */}
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                    Invoice URL
                  </label>
                  <input
                    type="url"
                    value={editData.invoice_url || ''}
                    onChange={(e) => setEditData({ ...editData, invoice_url: e.target.value })}
                    placeholder="https://example.com/invoice.pdf"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>

                {/* Key Project Dates */}
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '700', color: '#1E293B', marginBottom: '12px' }}>
                    Key Project Dates
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {/* Kickoff Date */}
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                        Kickoff Date
                      </label>
                      <input
                        type="date"
                        value={editData.kickoff_date || ''}
                        onChange={(e) => setEditData({ ...editData, kickoff_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    {/* First Draft Date */}
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                        First Draft Date
                      </label>
                      <input
                        type="date"
                        value={editData.first_draft_date || ''}
                        onChange={(e) => setEditData({ ...editData, first_draft_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    {/* Launch Start Date */}
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                        Launch Start Date
                      </label>
                      <input
                        type="date"
                        value={editData.launch_start_date || ''}
                        onChange={(e) => setEditData({ ...editData, launch_start_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>

                    {/* Launch End Date */}
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                        Launch End Date
                      </label>
                      <input
                        type="date"
                        value={editData.launch_end_date || ''}
                        onChange={(e) => setEditData({ ...editData, launch_end_date: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontFamily: 'inherit'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <button
                  onClick={handleSaveChanges}
                  disabled={saving}
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    padding: '12px',
                    background: saving ? '#CBD5E1' : '#10B981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'inherit'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>

              {/* Milestones Section */}
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #E2E8F0',
                marginBottom: '24px'
              }}>
                <MilestoneEditor
                  milestones={selectedClient.milestones || []}
                  clientId={selectedClient.client_id}
                  onUpdate={refreshClient}
                  backendUrl={BACKEND_URL}
                  adminKey={ADMIN_KEY}
                />
              </div>

              {/* Project Files & Uploads Section */}
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '1px solid #E2E8F0',
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#1E293B' }}>
                  Project Files & Uploads
                </h3>
                
                {/* Upload Section */}
                <div style={{ marginBottom: '24px' }}>
                  <FileUpload
                    clientId={selectedClient.client_id}
                    uploaderRole="admin"
                    uploaderName="BM-Assist"
                    onUploadComplete={(file) => {
                      showToast('File uploaded successfully');
                      refreshClient();
                    }}
                    onError={(error) => showToast(error, 'error')}
                    multiple={true}
                    compact={false}
                  />
                </div>

                {/* Files List with Admin Delete */}
                <FilesList
                  clientId={selectedClient.client_id}
                  adminKey={ADMIN_KEY}
                  onFileChange={(files) => {
                    // Optional: Update local state if needed
                  }}
                />
              </div>

              {/* Two Column Layout: Todos & Chat */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '24px' }}>
                {/* To-Dos Section */}
                <div style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid #E2E8F0'
                }}>
                  <TodoList
                    todos={selectedClient.client_todos || []}
                    onAdd={handleAddTodo}
                    onEdit={handleEditTodo}
                    onDelete={handleDeleteTodo}
                    onDuplicate={handleDuplicateTodo}
                  />
                </div>

                {/* Recent Updates (Client-Facing) */}
                <div style={{ marginBottom: '20px' }}>
                  <RecentUpdates
                    clientId={selectedClient.client_id}
                    backendUrl={BACKEND_URL}
                    adminKey={ADMIN_KEY}
                    onUpdate={refreshClient}
                  />
                </div>

                {/* Client Login Management */}
                <div style={{
                  background: '#fff',
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  border: '1px solid #E2E8F0',
                  marginBottom: '24px'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#1E293B' }}>
                    Client Login
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Login Status */}
                    <div style={{
                      padding: '12px',
                      background: '#F8FAFC',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#475569'
                    }}>
                      <strong>Status:</strong> {selectedClient.password_hash ? 'Password set' : 'No password set'}
                      {selectedClient.password_last_set && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#64748B' }}>
                          Last reset: {new Date(selectedClient.password_last_set).toLocaleString()}
                        </div>
                      )}
                      {selectedClient.failed_login_attempts > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#DC2626', fontWeight: '600' }}>
                          ⚠️ Failed login attempts: {selectedClient.failed_login_attempts}
                          {selectedClient.failed_login_attempts >= 5 && ' (Account locked)'}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                        onClick={handleResetPassword}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          background: '#4D7CFE',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#3B82F6'}
                        onMouseOut={(e) => e.target.style.background = '#4D7CFE'}
                      >
                        🔑 Reset Password
                      </button>

                      {selectedClient.failed_login_attempts > 0 && (
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/admin/client/${selectedClient.client_id}/unlock-account`, {
                                method: 'POST',
                                headers: {
                                  'x-admin-key': ADMIN_KEY
                                }
                              });

                              if (response.ok) {
                                showToast('Account unlocked successfully');
                                refreshClient();
                              } else {
                                showToast('Failed to unlock account', 'error');
                              }
                            } catch (err) {
                              console.error('Error unlocking account:', err);
                              showToast('Error unlocking account', 'error');
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '12px 16px',
                            background: '#10B981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                          onMouseOver={(e) => e.target.style.background = '#059669'}
                          onMouseOut={(e) => e.target.style.background = '#10B981'}
                        >
                          🔓 Unlock Account
                        </button>
                      )}
                    </div>

                    {/* Info Text */}
                    <div style={{
                      padding: '12px',
                      background: '#FEF3C7',
                      border: '1px solid #FCD34D',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#78350F'
                    }}>
                      ⚠️ Resetting the password will generate a new temporary password. Copy it and send it to the client - it won't be shown again.
                    </div>
                  </div>
                </div>

                {/* Internal Notes (Staff-Only) */}
                <div style={{ marginBottom: '20px' }}>
                  <InternalNotes
                    clientId={selectedClient.client_id}
                    backendUrl={BACKEND_URL}
                    adminKey={ADMIN_KEY}
                    onUpdate={refreshClient}
                  />
                </div>

                {/* Chat Panel (Chat v2) */}
                <div style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #E2E8F0'
                }}>
                  <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MessageCircle size={20} color="#667eea" />
                    Messages
                  </h3>
                  <SimpleChatThread
                    clientId={selectedClient.client_id}
                    mode="staff"
                    backendUrl={BACKEND_URL}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* To-Do Modal */}
      {showTodoModal && selectedClient && (
        <ClientTodoEditor
          isOpen={showTodoModal}
          onClose={() => {
            setShowTodoModal(false);
            setEditingTodo(null);
          }}
          editTodo={editingTodo}
          clientId={selectedClient.client_id}
          onUpdate={() => {
            refreshClient();
            showToast(editingTodo ? 'To-do updated successfully' : 'To-do created successfully');
          }}
          backendUrl={BACKEND_URL}
          adminKey={ADMIN_KEY}
        />
      )}

      {/* Password Reset Modal */}
      {passwordResetModal.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '700', color: '#1E293B' }}>
              🔑 Password Reset Successful
            </h3>
            
            <div style={{
              padding: '16px',
              background: '#F8FAFC',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '8px', fontWeight: '600' }}>
                New temporary password for {passwordResetModal.clientId}:
              </div>
              <div style={{
                padding: '12px',
                background: '#fff',
                border: '2px solid #4D7CFE',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1E293B',
                textAlign: 'center',
                letterSpacing: '2px'
              }}>
                {passwordResetModal.password}
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#FEF3C7',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#78350F',
              marginBottom: '20px'
            }}>
              ⚠️ <strong>Important:</strong> Copy this password now - it will not be shown again. Send it to your client via secure channel.
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={copyPasswordToClipboard}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#4D7CFE',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#3B82F6'}
                onMouseOut={(e) => e.target.style.background = '#4D7CFE'}
              >
                📋 Copy Password
              </button>
              <button
                onClick={() => setPasswordResetModal({ show: false, password: '', clientId: '' })}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: '#64748B',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#475569'}
                onMouseOut={(e) => e.target.style.background = '#64748B'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CAdminPage;
